import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { User } from "@/pages/Public/pages/Login/schema/login";
import { getLinkedinTier } from "@/utils/getLinkedinTier";

interface UseLinkedinInviteNoteParams {
	prospectId: number;
	hasConversations: boolean;
	lastSenderUserId?: number;
	isLastSenderConnected?: boolean;
	selectedSendingUser: User | null;
	connectedUserIds?: Set<number>;
	/** Senders whose own invitation to the prospect is pending (PREC-4216). */
	pendingInvitationUserIds?: Set<number>;
	prospectFirstName?: string | null;
}

/**
 * Owns the "Add note to LinkedIn invite" toggle state:
 *  - the selected sender is already connected to the prospect → a note is irrelevant, so the
 *    toggle is forced off and disabled (PREC-3570);
 *  - the selected sender's own invitation to the prospect is still pending → a second invitation
 *    cannot be sent, so no note is generated: forced off and disabled (PREC-4216);
 *  - the selected sender is on LinkedIn Free → the toggle stays usable but starts off. LinkedIn
 *    lets a Free account add a note to only a few connection requests a month, of at most 200
 *    characters. That is the rule the composer's note validation (`getInviteNoteSeverity`), the
 *    campaign send path and the help center apply, and a tooltip says so (PREC-4303: this bar
 *    used to block Free notes outright, claiming only Premium could add one).
 *
 * Otherwise the default mirrors the previous behaviour: enabled, unless there is
 * conversation history with a known, already-connected last sender.
 */
export const useLinkedinInviteNote = ({
	prospectId,
	hasConversations,
	lastSenderUserId,
	isLastSenderConnected,
	selectedSendingUser,
	connectedUserIds,
	pendingInvitationUserIds,
	prospectFirstName,
}: UseLinkedinInviteNoteParams) => {
	const { t } = useTranslation();

	const [linkedinInviteNote, setLinkedinInviteNote] = useState<boolean>(
		// When there's conversation history and a known sender, preset based on connection status:
		// connected → no invite note needed; not connected → include invite note
		hasConversations && lastSenderUserId != null ? !isLastSenderConnected : true
	);

	// When the currently selected sender is already connected to the prospect on
	// LinkedIn, an invite note is irrelevant.
	const isSelectedSenderConnected = useMemo(
		() => !!selectedSendingUser && !!connectedUserIds?.has(selectedSendingUser.id),
		[selectedSendingUser, connectedUserIds]
	);

	// Their invitation is pending: the message waits for it, there is no second one (PREC-4216).
	const isSelectedSenderPending =
		!!selectedSendingUser && !!pendingInvitationUserIds?.has(selectedSendingUser.id);

	// A Free sender may add a note, but only to a few invitations a month (PREC-4303).
	const isSelectedSenderFreeLinkedin = useMemo(
		() => !!selectedSendingUser && getLinkedinTier(selectedSendingUser) === "free",
		[selectedSendingUser]
	);

	const linkedinInviteNoteDisabled = isSelectedSenderConnected || isSelectedSenderPending;

	const linkedinInviteNoteTooltip = useMemo(() => {
		if (isSelectedSenderConnected && selectedSendingUser && prospectFirstName) {
			return t("messages.generateBar.inviteNoteAlreadyConnected", {
				sender: selectedSendingUser.firstName,
				prospect: prospectFirstName,
			});
		}

		if (isSelectedSenderPending && selectedSendingUser && prospectFirstName) {
			return t("messages.generateBar.inviteNoteInvitationPending", {
				sender: selectedSendingUser.firstName,
				prospect: prospectFirstName,
			});
		}

		if (isSelectedSenderFreeLinkedin && selectedSendingUser) {
			return t("messages.generateBar.inviteNoteFreeQuota", {
				sender: selectedSendingUser.firstName,
			});
		}

		return null;
	}, [
		isSelectedSenderConnected,
		isSelectedSenderPending,
		isSelectedSenderFreeLinkedin,
		selectedSendingUser,
		prospectFirstName,
		t,
	]);

	// Reset the toggle when the prospect, the selected-sender connection state, or
	// the sender's LinkedIn tier changes.
	useEffect(() => {
		if (linkedinInviteNoteDisabled || isSelectedSenderFreeLinkedin) {
			setLinkedinInviteNote(false);

			return;
		}

		setLinkedinInviteNote(
			hasConversations && lastSenderUserId != null ? !isLastSenderConnected : true
		);
	}, [
		prospectId,
		hasConversations,
		lastSenderUserId,
		isLastSenderConnected,
		linkedinInviteNoteDisabled,
		isSelectedSenderFreeLinkedin,
	]);

	return {
		linkedinInviteNote,
		setLinkedinInviteNote,
		linkedinInviteNoteDisabled,
		linkedinInviteNoteTooltip,
	};
};
